import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spin, Button, Card, Typography, Input, InputNumber, Select, Alert, theme } from 'antd';
import { IconAlertTriangle, IconTrash, IconArrowLeft } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { message } from '../../services/toastMessage';
import AppLayout from '../../components/Layouts/Layout';
import { getRateSheet, confirmRateSheet } from '../../services/ProviderRateSheetService';
import {
  IProviderRateDocumentReview, IParsedRateRow, IConfirmRateRow,
  RateItemKind, RATE_ITEM_UNITS,
} from '../../interfaces/heavyDutyMachinery/IProviderRateSheet';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { SURFACE_1_SOFT } from '../../theme/creamyTokens';

const { Text, Title } = Typography;

/** Below this, the parser itself was unsure. Those rows carry a visible mark. */
const LOW_CONFIDENCE = 0.7;

const KIND_LABEL_KEYS: Record<RateItemKind, string> = {
  Material: TRANSLATIONS.RATE_ITEM_KIND_MATERIAL,
  Labour: TRANSLATIONS.RATE_ITEM_KIND_LABOUR,
  Travel: TRANSLATIONS.RATE_ITEM_KIND_TRAVEL,
};

/** A parsed row plus the two things editing it needs: a stable key and whether it survives. */
interface EditableRow extends IParsedRateRow {
  key: string;
  /** The VAT-inclusive figure, which is what gets sent and what the catalogue stores. */
  price: number | null;
  included: boolean;
}

/**
 * The screen between a machine reading a PDF and a price somebody can quote
 * off. Nothing this page shows is real yet.
 *
 * It exists because supplier price lists are a mess -- repeated headers,
 * double columns, discount tables in the footer -- and a parser that eats one
 * decimal point produces a quote that loses its author money on a job they
 * have already committed to. So every row is editable, every row can be
 * dropped, and nothing is written until the button at the bottom.
 *
 * Three things are surfaced rather than buried, because each is a reason to
 * look harder at a specific row:
 *
 * - **Low confidence.** The parser says it was not sure. Server-sorted to the
 *   top, marked here.
 * - **No price found.** Left blank on purpose -- the parser is told never to
 *   invent one. A blank row cannot be confirmed until it is filled or dropped.
 * - **Collides with a price you fixed by hand.** The upload will not overwrite
 *   it, and saying so before the confirmation beats explaining it after.
 */
const RateSheetReviewContent: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [review, setReview] = useState<IProviderRateDocumentReview | null>(null);
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getRateSheet(Number(id));
      setReview(data);
      setRows(data.rows.map((row, index) => ({
        ...row,
        key: `${index}`,
        // The VAT-inclusive figure is the one on screen and the one that
        // travels back -- see IConfirmRateRow. A row the parser could not
        // price starts blank rather than at zero, which is a real price.
        price: row.unitPriceWithVat ?? null,
        // A row that collides with a hand-fixed price is off by default:
        // confirming it would do nothing anyway (the server protects the
        // corrected one), so leaving it ticked would only be misleading.
        included: row.conflictsWithCorrectedPrice === undefined || row.conflictsWithCorrectedPrice === null,
      })));
    } catch {
      message.error(translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_LOAD_ERROR));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { load(); }, [load]);

  const patch = (key: string, changes: Partial<EditableRow>) =>
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...changes } : row)));

  const selected = useMemo(() => rows.filter((row) => row.included), [rows]);
  const missingPrice = useMemo(
    () => selected.filter((row) => row.price === null || row.price <= 0).length,
    [selected],
  );

  const handleConfirm = async () => {
    if (!id) return;
    if (selected.length === 0) {
      message.error(translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_NOTHING_SELECTED));
      return;
    }
    if (missingPrice > 0) {
      message.error(translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_MISSING_PRICES, { count: missingPrice }));
      return;
    }

    const payload: IConfirmRateRow[] = selected.map((row) => ({
      reference: row.reference,
      kind: row.kind,
      title: row.title,
      subtitle: row.subtitle,
      category: row.category,
      unit: row.unit,
      unitPrice: row.price as number,
    }));

    setSaving(true);
    try {
      const result = await confirmRateSheet(Number(id), payload);
      message.success(translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_CONFIRMED, {
        created: result.created,
        updated: result.updated,
      }));
      // Reported rather than swallowed: a number that did not change when
      // somebody expected it to is worth saying out loud.
      if (result.skippedManuallyCorrected > 0) {
        message.info(translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_SKIPPED, {
          count: result.skippedManuallyCorrected,
        }));
      }
      navigate('/heavy-duty-machinery/my-rates');
    } catch (error: any) {
      message.error(error?.message || translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_SAVE_ERROR));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!review) return null;

  return (
    <div style={{ paddingBottom: 96 }}>
      <Button type="text" icon={<IconArrowLeft size={16} />} onClick={() => navigate('/heavy-duty-machinery/my-rates')}>
        {translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_BACK)}
      </Button>

      <Title level={4} style={{ marginTop: 10, marginBottom: 2 }}>
        {translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_TITLE)}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 14, wordBreak: 'break-word' }}>
        {review.document.fileName}
      </Text>

      <Alert
        type="info"
        showIcon
        style={{ borderRadius: 12, marginBottom: 8 }}
        message={translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_NOTICE)}
      />

      {!review.document.pricesIncludeVat && (
        <Alert
          type="warning"
          showIcon
          style={{ borderRadius: 12, marginBottom: 8 }}
          message={translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_VAT_ADDED, { rate: review.vatRatePercent })}
        />
      )}

      <Text type="secondary" style={{ display: 'block', fontSize: 13, margin: '10px 0 14px' }}>
        {translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_COUNT, { selected: selected.length, total: rows.length })}
      </Text>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((row) => {
          const unsure = row.confidence < LOW_CONFIDENCE;
          const conflicts = row.conflictsWithCorrectedPrice !== undefined && row.conflictsWithCorrectedPrice !== null;
          return (
            <Card
              key={row.key}
              size="small"
              style={{
                borderRadius: 14,
                opacity: row.included ? 1 : 0.5,
                borderColor: unsure && row.included ? token.colorWarning : undefined,
                background: row.included ? undefined : SURFACE_1_SOFT,
              }}
              styles={{ body: { padding: 14 } }}
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                <Input
                  value={row.title}
                  onChange={(e) => patch(row.key, { title: e.target.value })}
                  style={{ borderRadius: 10, fontWeight: 500 }}
                />
                <Button
                  size="small"
                  danger={row.included}
                  icon={<IconTrash size={14} />}
                  onClick={() => patch(row.key, { included: !row.included })}
                  title={translate(t, row.included ? TRANSLATIONS.RATE_SHEET_REVIEW_DROP_ROW : TRANSLATIONS.RATE_SHEET_REVIEW_KEEP_ROW)}
                />
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Input
                  value={row.reference}
                  onChange={(e) => patch(row.key, { reference: e.target.value })}
                  placeholder={translate(t, TRANSLATIONS.RATE_ITEM_REFERENCE_LABEL)}
                  style={{ borderRadius: 10, flex: isMobile ? '1 1 100%' : '1 1 140px', minWidth: 120 }}
                />
                <Select
                  value={row.kind}
                  onChange={(kind: RateItemKind) => patch(row.key, { kind })}
                  style={{ flex: '1 1 130px', minWidth: 120 }}
                  options={(Object.keys(KIND_LABEL_KEYS) as RateItemKind[]).map((kind) => ({
                    value: kind,
                    label: translate(t, KIND_LABEL_KEYS[kind]),
                  }))}
                />
                <Select
                  value={row.unit}
                  onChange={(unit: string) => patch(row.key, { unit })}
                  style={{ flex: '0 1 90px', minWidth: 80 }}
                  options={RATE_ITEM_UNITS.map((unit) => ({ value: unit, label: unit }))}
                />
                <InputNumber
                  value={row.price}
                  onChange={(value) => patch(row.key, { price: value })}
                  min={0}
                  step={0.5}
                  addonAfter="€"
                  // Blank, not zero: the parser is told never to invent a
                  // price, and zero is a real one.
                  placeholder={translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_NO_PRICE)}
                  status={row.included && (row.price === null || row.price <= 0) ? 'error' : undefined}
                  style={{ flex: '1 1 140px', minWidth: 130 }}
                />
              </div>

              {(unsure || row.warning || conflicts) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                  {unsure && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: token.colorWarningBg, color: token.colorWarning, borderRadius: 999, padding: '4px 10px', fontSize: 12, alignSelf: 'flex-start' }}>
                      <IconAlertTriangle size={13} />
                      {translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_UNSURE)}
                    </span>
                  )}
                  {row.warning && (
                    <Text type="secondary" style={{ fontSize: 12 }}>{row.warning}</Text>
                  )}
                  {conflicts && (
                    <Text style={{ fontSize: 12, color: token.colorWarning }}>
                      {translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_CONFLICT, {
                        price: row.conflictsWithCorrectedPrice?.toFixed(2),
                      })}
                    </Text>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Pinned, because the list is long and the decision is at the end of
          it. Nothing on this page has been written until this is pressed. */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          padding: `12px 20px calc(12px + env(safe-area-inset-bottom, 0px))`,
          background: token.colorBgContainer,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          zIndex: 20,
        }}
      >
        <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 13, flex: 1 }}>
            {translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_COUNT, { selected: selected.length, total: rows.length })}
          </Text>
          <Button
            type="primary"
            shape="round"
            size="large"
            loading={saving}
            onClick={handleConfirm}
            style={{ minWidth: 160 }}
          >
            {translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_CONFIRM_CTA)}
          </Button>
        </div>
      </div>
    </div>
  );
};

const RateSheetReview: React.FC = () => (
  <AppLayout>
    <RateSheetReviewContent />
  </AppLayout>
);

export default RateSheetReview;
